import { Button } from "@components/shadcn-ui/button";
import { Card, CardHeader, CardContent } from "@components/shadcn-ui/card";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@components/shadcn-ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/shadcn-ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/shadcn-ui/table";
import { Form } from "@components/shadcn-ui/form";
import { useForm } from "react-hook-form";
import { DynamicFormField } from "@components/ui/DynamicInput";
import { LOAN_REQUIREMENT_DETAILS } from "features/loanApplications/formFieldsConfig/loanRequirementDetails";
import { useState } from "react";

function pmt(rate: number, nper: number, pv: number) {
  if (rate === 0) {
    return pv / nper;
  } else {
    const numerator = rate * pv * Math.pow(1 + rate, nper);
    const denominator = Math.pow(1 + rate, nper) - 1;
    return numerator / denominator;
  }
}

export function EmiCalculator({
  floatingCard,
  className,
}: {
  floatingCard?: boolean;
  handleOptionClick?: () => void;
  className?: string;
}) {
  const form = useForm();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [emi, setEmi] = useState<number | null>(null);
  const [totalRepayment, setTotalRepayment] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [flatRatePA, setFlatRatePA] = useState<number | null>(null);
  const [flatRatePM, setFlatRatePM] = useState<number | null>(null);
  const [interestRepaidPM, setInterestRepaidPM] = useState<number | null>(null);

  async function handleCalculate() {
    const isValid = await form.trigger();
    if (!isValid) return;

    const {
      amount: loanAmount,
      rateOfInterest: annualIRR,
      tenureInMonths: tenure,
    } = form.getValues();

    const principal = parseFloat(loanAmount) || 0;
    const decimalAnnualIRR = (parseFloat(annualIRR) || 0) / 100;
    const months = parseInt(tenure) || 1;

    const monthlyRate = decimalAnnualIRR / 12;
    const calculatedEmi = pmt(monthlyRate, months, principal);

    const repayment = Math.round(calculatedEmi * months);
    const interest = repayment - principal;
    const flatPA = (interest / principal) * (12 / months) * 100;
    const flatPM = flatPA / 12;
    const interestPM = interest / months;

    setEmi(calculatedEmi);
    setTotalRepayment(repayment);
    setTotalInterest(interest);
    setFlatRatePA(flatPA);
    setFlatRatePM(flatPM);
    setInterestRepaidPM(interestPM);

    // Open the results popup after calculation
    setIsResultsDialogOpen(true);
  }

  function handleReset() {
    form.reset({
      amount: "",
      rateOfInterest: "",
      tenureInMonths: "",
    });
    setEmi(null);
    setTotalRepayment(null);
    setTotalInterest(null);
    setFlatRatePA(null);
    setFlatRatePM(null);
    setInterestRepaidPM(null);
  }

  // Update required flag in LOAN_REQUIREMENT_DETAILS as we are using the same for loan creation form
  const LOAN_REQUIREMENT_DETAILS_REQUIRED = LOAN_REQUIREMENT_DETAILS.fields.map(
    (field) => (field.validationRules.required = true)
  );

  return (
    <>
      {floatingCard ? (
        <Card
          className={`bg-[#BEA0A0] flex items-center px-3 py-2 w-fit ${className}`}
          onClick={() => {
            setIsDrawerOpen(true);
          }}
        >
          <img
            src={"/assets/emiCalculator.svg"}
            alt=""
            style={{ height: "24px" }}
          />
          <CardContent className="p-0 ml-4">EMI Calculator</CardContent>
        </Card>
      ) : (
        <Card
          className={`bg-[#BEA0A0] rounded-3xl shadow flex pb-0 flex-col justify-between h-full items-center cursor-pointer`}
          onClick={() => setIsDrawerOpen(true)}
        >
          <CardHeader className="text-lg pb-4 font-semibold">
            EMI Calculator
          </CardHeader>
          <div className="icon mb-2 h-[100px]">
            <img src={"/assets/emiCalculator.svg"} alt="" />
          </div>
        </Card>
      )}

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-h-[100vh] h-fit w-[100vw]">
          <DrawerHeader className="pb-2 flex justify-center">
            <DrawerTitle>EMI Calculator</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto">
            <div className="grid gap-4 p-4">
              <Form {...form}>
                {LOAN_REQUIREMENT_DETAILS.fields.map((field) => (
                  <DynamicFormField
                    key={field.name}
                    field={field}
                    isDisabled={false}
                  />
                ))}
              </Form>
              <div className="flex gap-4 items-center justify-center">
                <Button
                  onClick={handleCalculate}
                  disabled={!form.formState.isValid}
                >
                  Calculate EMI
                </Button>
                <Button onClick={handleReset}>Reset</Button>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
        <DialogContent className="max-h-[100vh] h-fit w-[100vw]">
          <DialogHeader className="pb-2 p-6 flex justify-center">
            <DialogTitle>EMI Calculation Results</DialogTitle>
          </DialogHeader>
          <div className="px-4">
            {emi !== null && (
              <Table className="mb-6 w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="bg-primary text-primary-foreground">
                      Measure
                    </TableHead>
                    <TableHead className="bg-primary text-primary-foreground">
                      Value
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>EMI</TableCell>
                    <TableCell>
                      <strong>{Math.round(emi).toLocaleString("en-IN")}</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-[#eceaea]">
                    <TableCell>Total Repayment</TableCell>
                    <TableCell>
                      <strong>
                        {Math.round(totalRepayment || 0).toLocaleString(
                          "en-IN"
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total Interest</TableCell>
                    <TableCell>
                      <strong>
                        {Math.round(totalInterest || 0).toLocaleString("en-IN")}
                      </strong>
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-[#eceaea]">
                    <TableCell>Flat Rate p.a.</TableCell>
                    <TableCell>
                      <strong>{flatRatePA?.toFixed(2)}%</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Flat Rate p.m.</TableCell>
                    <TableCell>
                      <strong>{flatRatePM?.toFixed(2)}%</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-[#eceaea]">
                    <TableCell>Interest Repaid p.m.</TableCell>
                    <TableCell>
                      <strong>
                        {Math.round(interestRepaidPM || 0).toLocaleString(
                          "en-IN"
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
